import { useState } from "react";
import { Calculator, TrendingUp, PieChart, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Slider } from "../components/ui/slider";
import { Link } from "react-router-dom";

const formatPrice = (price) => {
  if (price >= 10000000) {
    return `₹${(price / 10000000).toFixed(2)} Cr`;
  } else if (price >= 100000) {
    return `₹${(price / 100000).toFixed(2)} L`;
  }
  return `₹${price.toLocaleString("en-IN")}`;
};

const EMICalculatorPage = () => {
  const [loanAmount, setLoanAmount] = useState(5000000);
  const [interestRate, setInterestRate] = useState(8.5);
  const [tenure, setTenure] = useState(240);

  const calculateEMI = () => {
    const P = loanAmount;
    const r = interestRate / 12 / 100;
    const n = tenure;

    if (r === 0) return P / n;
    return (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
  };

  const emi = calculateEMI();
  const totalPayment = emi * tenure;
  const totalInterest = totalPayment - loanAmount;

  const principalPercent = (loanAmount / totalPayment) * 100;
  const interestPercent = (totalInterest / totalPayment) * 100;

  return (
    <div className="min-h-screen pt-20 bg-stone-50" data-testid="emi-calculator-page">
      {/* Hero */}
      <section className="bg-emerald-900 py-16">
        <div className="section-container">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-widest text-emerald-300 font-medium mb-3">Financial Tools</p>
            <h1 className="font-display text-3xl md:text-5xl text-white mb-4">EMI Calculator</h1>
            <p className="text-emerald-100/70 text-lg">
              Plan your home purchase with our easy-to-use EMI calculator. Get instant estimates for your monthly payments.
            </p>
          </div>
        </div>
      </section>

      {/* Calculator */}
      <section className="section-spacing">
        <div className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Input Section */}
            <div className="bg-white p-8 rounded-sm shadow-layered" data-testid="emi-inputs">
              <h2 className="font-display text-xl text-stone-900 mb-6 flex items-center gap-2">
                <Calculator className="w-5 h-5" strokeWidth={1.5} />
                Calculate Your EMI
              </h2>

              {/* Loan Amount */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-xs uppercase tracking-widest text-stone-500">Loan Amount</label>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-lg text-stone-900">{formatPrice(loanAmount)}</span>
                  </div>
                </div>
                <Slider
                  value={[loanAmount]}
                  onValueChange={(value) => setLoanAmount(value[0])}
                  min={500000}
                  max={100000000}
                  step={100000}
                  className="py-4"
                  data-testid="loan-amount-slider"
                />
                <div className="flex justify-between text-xs text-stone-400 mt-1">
                  <span>₹5L</span>
                  <span>₹10Cr</span>
                </div>
              </div>

              {/* Interest Rate */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-xs uppercase tracking-widest text-stone-500">Interest Rate (% p.a.)</label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={interestRate}
                      onChange={(e) => setInterestRate(parseFloat(e.target.value) || 0)}
                      className="w-20 text-right font-mono border-stone-200"
                      step={0.1}
                      min={5}
                      max={15}
                      data-testid="interest-rate-input"
                    />
                    <span className="text-stone-500">%</span>
                  </div>
                </div>
                <Slider
                  value={[interestRate]}
                  onValueChange={(value) => setInterestRate(value[0])}
                  min={5}
                  max={15}
                  step={0.1}
                  className="py-4"
                  data-testid="interest-rate-slider"
                />
                <div className="flex justify-between text-xs text-stone-400 mt-1">
                  <span>5%</span>
                  <span>15%</span>
                </div>
              </div>

              {/* Tenure */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-xs uppercase tracking-widest text-stone-500">Loan Tenure</label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={tenure / 12}
                      onChange={(e) => setTenure((parseInt(e.target.value) || 1) * 12)}
                      className="w-16 text-right font-mono border-stone-200"
                      min={1}
                      max={30}
                      data-testid="tenure-input"
                    />
                    <span className="text-stone-500">Years</span>
                  </div>
                </div>
                <Slider
                  value={[tenure]}
                  onValueChange={(value) => setTenure(value[0])}
                  min={12}
                  max={360}
                  step={12}
                  className="py-4"
                  data-testid="tenure-slider"
                />
                <div className="flex justify-between text-xs text-stone-400 mt-1">
                  <span>1 Year</span>
                  <span>30 Years</span>
                </div>
              </div>

              {/* Quick Presets */}
              <div className="pt-6 border-t border-stone-100">
                <p className="text-xs uppercase tracking-widest text-stone-500 mb-3">Quick Presets</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { amount: 3000000, label: "₹30L" },
                    { amount: 5000000, label: "₹50L" },
                    { amount: 10000000, label: "₹1Cr" },
                    { amount: 20000000, label: "₹2Cr" },
                  ].map((preset) => (
                    <button
                      key={preset.amount}
                      onClick={() => setLoanAmount(preset.amount)}
                      className={`px-4 py-2 text-sm border rounded-sm transition-colors ${
                        loanAmount === preset.amount
                          ? "bg-emerald-900 text-white border-emerald-900"
                          : "border-stone-200 hover:border-emerald-900"
                      }`}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Results Section */}
            <div className="space-y-6">
              {/* EMI Result */}
              <div className="bg-emerald-900 p-8 rounded-sm text-white" data-testid="emi-result">
                <p className="text-xs uppercase tracking-widest text-emerald-300 mb-2">Your Monthly EMI</p>
                <p className="font-mono text-4xl md:text-5xl mb-4">{formatPrice(Math.round(emi))}</p>
                <p className="text-emerald-100/70">
                  for {tenure / 12} years at {interestRate}% interest
                </p>
              </div>

              {/* Breakdown */}
              <div className="bg-white p-8 rounded-sm shadow-layered" data-testid="emi-breakdown">
                <h3 className="font-display text-lg text-stone-900 mb-6 flex items-center gap-2">
                  <PieChart className="w-5 h-5" strokeWidth={1.5} />
                  Payment Breakdown
                </h3>

                {/* Visual Bar */}
                <div className="h-8 rounded-sm overflow-hidden flex mb-6">
                  <div
                    className="bg-emerald-600 transition-all duration-500"
                    style={{ width: `${principalPercent}%` }}
                  />
                  <div
                    className="bg-amber-500 transition-all duration-500"
                    style={{ width: `${interestPercent}%` }}
                  />
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-emerald-600 rounded-sm" />
                      <span className="text-stone-600">Principal Amount</span>
                    </div>
                    <span className="font-mono text-stone-900">{formatPrice(loanAmount)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-amber-500 rounded-sm" />
                      <span className="text-stone-600">Total Interest</span>
                    </div>
                    <span className="font-mono text-stone-900">{formatPrice(Math.round(totalInterest))}</span>
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-stone-100">
                    <span className="font-medium text-stone-900">Total Payment</span>
                    <span className="font-mono text-lg text-stone-900">{formatPrice(Math.round(totalPayment))}</span>
                  </div>
                </div>
              </div>

              {/* Tips */}
              <div className="bg-stone-100 p-6 rounded-sm">
                <h4 className="font-medium text-stone-900 mb-3 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" strokeWidth={1.5} />
                  Pro Tips
                </h4>
                <ul className="space-y-2 text-sm text-stone-600">
                  <li>• Higher down payment = Lower EMI and interest</li>
                  <li>• Compare rates from multiple banks before applying</li>
                  <li>• Consider prepayment options to save on interest</li>
                  <li>• Factor in additional costs like registration, GST</li>
                </ul>
              </div>

              {/* CTA */}
              <Link
                to="/search"
                className="btn-primary w-full flex items-center justify-center gap-2"
                data-testid="browse-properties-btn"
              >
                Browse Properties in Your Budget <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="section-spacing bg-white">
        <div className="section-container">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-display text-2xl md:text-3xl text-stone-900 mb-6">How EMI is Calculated</h2>
            <p className="text-stone-600 mb-6">
              EMI (Equated Monthly Installment) is calculated using the formula:
            </p>
            <div className="bg-stone-50 p-6 rounded-sm font-mono text-center text-lg mb-6">
              EMI = P × r × (1+r)<sup>n</sup> / [(1+r)<sup>n</sup> - 1]
            </div>
            <p className="text-stone-600 mb-4">Where:</p>
            <ul className="space-y-2 text-stone-600">
              <li><strong>P</strong> = Principal loan amount</li>
              <li><strong>r</strong> = Monthly interest rate (Annual rate / 12 / 100)</li>
              <li><strong>n</strong> = Loan tenure in months</li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EMICalculatorPage;
